// src/utils/apiUsageTracker.js

/**
 * Simple API usage tracking utility to monitor FMP API calls
 */
class ApiUsageTracker {
  constructor() {
    // Try to get stored values
    this.initializeFromStorage();

    // Start checking for day changes
    this.startDayChangeCheck();
  }

  initializeFromStorage() {
    try {
      // Get values from local storage
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      const storedData = JSON.parse(
        localStorage.getItem("fmpApiUsage") || "{}"
      );

      // Check if we need to reset for a new day
      if (storedData.date !== today) {
        this.resetUsage(today);
      } else {
        this.callsUsedToday = storedData.callsUsedToday || 0;
        this.date = storedData.date;
        this.dailyLimit = storedData.dailyLimit || 250;
      }
    } catch (error) {
      console.error("Error initializing API usage tracker:", error);
      this.resetUsage();
    }
  }

  /**
   * Reset the API usage counter for a new day
   * @param {string} [date] - Date string in YYYY-MM-DD format
   */
  resetUsage(date = new Date().toISOString().split("T")[0]) {
    this.callsUsedToday = 0;
    this.date = date;
    this.dailyLimit = 250; // Default daily limit for FMP free tier
    this.saveToStorage();
  }

  /**
   * Record an API call
   * @param {number} [count=1] - Number of API calls to record
   */
  recordApiCall(count = 1) {
    this.callsUsedToday += count;
    this.saveToStorage();

    // Dispatch an event for components to listen to
    const event = new CustomEvent("apiUsageUpdated", {
      detail: {
        callsUsedToday: this.callsUsedToday,
        callsRemaining: this.getCallsRemaining(),
      },
    });
    window.dispatchEvent(event);

    return this.callsUsedToday;
  }

  /**
   * Get the number of API calls used today
   * @returns {number} Calls used today
   */
  getCallsUsedToday() {
    return this.callsUsedToday;
  }

  /**
   * Get the number of API calls remaining for today
   * @returns {number} Calls remaining
   */
  getCallsRemaining() {
    return Math.max(0, this.dailyLimit - this.callsUsedToday);
  }

  /**
   * Get the percentage of API calls used today
   * @returns {number} Percentage used (0-100)
   */
  getUsagePercentage() {
    return Math.min(100, (this.callsUsedToday / this.dailyLimit) * 100);
  }

  /**
   * Check if we have enough API calls remaining for a given operation
   * @param {number} [requiredCalls=1] - Number of calls required
   * @returns {boolean} True if enough calls are available
   */
  hasEnoughCalls(requiredCalls = 1) {
    return this.getCallsRemaining() >= requiredCalls;
  }

  /**
   * Save current usage data to local storage
   */
  saveToStorage() {
    const data = {
      callsUsedToday: this.callsUsedToday,
      date: this.date,
      dailyLimit: this.dailyLimit,
    };

    localStorage.setItem("fmpApiUsage", JSON.stringify(data));
  }

  /**
   * Set the daily limit for API calls
   * @param {number} limit - New daily limit
   */
  setDailyLimit(limit) {
    this.dailyLimit = limit;
    this.saveToStorage();
  }

  /**
   * Start periodic check for day changes to reset the counter
   */
  startDayChangeCheck() {
    // Check every hour if the day has changed
    setInterval(() => {
      const today = new Date().toISOString().split("T")[0];
      if (this.date !== today) {
        this.resetUsage(today);
        console.log("API usage tracker reset for new day:", today);
      }
    }, 60 * 60 * 1000); // Check every hour
  }
}

// Create and export a singleton instance
export const apiUsageTracker = new ApiUsageTracker();
